import React, { useState } from 'react';
import './VirtualMallForgetPassword.scss';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { virtualMallForgetPasswordApi, virtualMallVerifyPhoneNumberApi, virtualMallChangePasswordApi } from './VirtualMallApi/api';

const VirtualMallForgetPassword = () => {
    const navigate = useNavigate();
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        referal_id: '',
        phone_number: '',
        password: '',
        confirm_password: ''
    });
    const [showPassword, setShowPassword] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    // Step 1: Verify Referral ID
    const handleReferralSubmit = async (e) => {
        e.preventDefault();
        if (!formData.referal_id.trim()) {
            toast.error('Referral ID is required');
            return;
        }
        setLoading(true);
        try {
            const response = await virtualMallForgetPasswordApi({ referal_id: formData.referal_id });
            if (response.result === true) {
                toast.success(response.message);
                setStep(2);
            } else {
                toast.error(response.message || "Invalid Referral ID");
            }
        } catch (error) {
            console.error(error);
            toast.error(error.response?.data?.message || "Failed to verify Referral ID");
        } finally {
            setLoading(false);
        }
    };

    // Step 2: Verify Phone Number
    const handlePhoneSubmit = async (e) => {
        e.preventDefault();
        if (!formData.phone_number.trim()) {
            toast.error('Phone number is required');
            return;
        }
        setLoading(true);
        try {
            const payload = {
                referal_id: formData.referal_id,
                phone_number: formData.phone_number
            };
            const response = await virtualMallVerifyPhoneNumberApi(payload);
            if (response.result === true) {
                toast.success(response.message);
                setStep(3);
            } else {
                toast.error(response.message || "Verification failed");
            }
        } catch (error) {
            console.error(error);
            toast.error(error.response?.data?.message || "Verification failed");
        } finally {
            setLoading(false);
        }
    };

    // Step 3: Reset Password
    const handlePasswordSubmit = async (e) => {
        e.preventDefault();
        if (formData.password !== formData.confirm_password) {
            toast.error('Passwords do not match');
            return;
        }
        if (formData.password.length < 6) {
            toast.error('Password must be at least 6 characters');
            return;
        }
        setLoading(true);
        try {
            const payload = {
                referal_id: formData.referal_id,
                password: formData.password
            };
            // Note: Reuse change password api as requested, passing referal_id manually
            const response = await virtualMallChangePasswordApi(payload);

            // Check for 'result' as per this specific flow response
            if (response.result === true || response.status === true) {
                toast.success(response.message || "Password has been changed successfully");
                navigate('/virtual_mall_login');
            } else {
                toast.error(response.message || "Failed to reset password");
            }
        } catch (error) {
            console.error(error);
            toast.error(error.response?.data?.message || "Failed to reset password");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="virtual-mall-forget-password">
            <div className="forget-container">
                <div className="forget-card">
                    <h2 className="forget-title">
                        {step === 1 && "Forgot Password"}
                        {step === 2 && "Verification"}
                        {step === 3 && "Reset Password"}
                    </h2>

                    {step === 1 && (
                        <form onSubmit={handleReferralSubmit}>
                            <div className="form-group">
                                <label className="form-label">Referral ID</label>
                                <input
                                    type="text"
                                    name="referal_id"
                                    className="form-input"
                                    placeholder="Enter your Referral ID"
                                    value={formData.referal_id}
                                    onChange={handleChange}
                                    disabled={loading}
                                />
                            </div>
                            <button type="submit" className="action-button" disabled={loading}>
                                {loading ? "Verifying..." : "Next"}
                            </button>
                        </form>
                    )}

                    {step === 2 && (
                        <form onSubmit={handlePhoneSubmit}>
                            <div className="form-group">
                                <label className="form-label">Phone Number</label>
                                <input
                                    type="tel"
                                    name="phone_number"
                                    className="form-input"
                                    placeholder="Enter your registered Phone Number"
                                    value={formData.phone_number}
                                    onChange={handleChange}
                                    disabled={loading}
                                    maxLength="10"
                                />
                            </div>
                            <button type="submit" className="action-button" disabled={loading}>
                                {loading ? "Verifying..." : "Verify"}
                            </button>
                        </form>
                    )}

                    {step === 3 && (
                        <form onSubmit={handlePasswordSubmit}>
                            <div className="form-group">
                                <label className="form-label">New Password</label>
                                <div className="password-input">
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        name="password"
                                        className="form-input"
                                        placeholder="Enter new password"
                                        value={formData.password}
                                        onChange={handleChange}
                                        disabled={loading}
                                    />
                                    <button
                                        type="button"
                                        className="password-toggle"
                                        onClick={() => setShowPassword(!showPassword)}
                                    >
                                        {showPassword ? '🙈' : '👁️'}
                                    </button>
                                </div>
                            </div>
                            <div className="form-group">
                                <label className="form-label">Confirm Password</label>
                                <input
                                    type="password"
                                    name="confirm_password"
                                    className="form-input"
                                    placeholder="Confirm new password"
                                    value={formData.confirm_password}
                                    onChange={handleChange}
                                    disabled={loading}
                                />
                            </div>
                            <button type="submit" className="action-button" disabled={loading}>
                                {loading ? "Resetting..." : "Reset Password"}
                            </button>
                        </form>
                    )}

                    <div className="back-link-container">
                        <button type="button" className="back-link" onClick={() => navigate('/virtual_mall_login')}>
                            Back to Login
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default VirtualMallForgetPassword;
